import React, { useState, useEffect, useMemo, useRef } from 'react';
import { functions } from '../firebaseConfig';
import { httpsCallable } from "firebase/functions";
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, AlertTriangle, RefreshCw, Lock } from 'lucide-react';
import Navbar from '../components/Navbar';
import AstroInsightButton from '../components/AstroInsightButton';
import InsightSidebar from '../components/InsightSidebar';
import SignosSection from '../components/SignosSection';
import ChartWheel from '../components/ChartWheel';
import HousesSection from '../components/HousesSection';
import PlanetasSection from '../components/PlanetasSection';
import { useAuth } from '../contexts/AuthContext';
import { getUserFriendlyMessage } from '../utils/errorMessages';
import { usePlan } from '../contexts/PlanContext';
import Button from '../components/ui/Button';

const AstroChartDisplay = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { limits, isAtLimit } = usePlan();
  const isSummaryOnly = limits.analysis_type === 'summary';
  const justGenerated = location.state?.justGenerated || false;
  const [report, setReport] = useState(null);
  const [svgChart, setSvgChart] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [chartError, setChartError] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [reportNotFound, setReportNotFound] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);
  const [retryingAnalysis, setRetryingAnalysis] = useState(false);
  const [analysisGenerated, setAnalysisGenerated] = useState(null);
  const [awaitingCreation, setAwaitingCreation] = useState(justGenerated);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [preloadedMessages, setPreloadedMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [loadingReport, setLoadingReport] = useState(true);
  const [loadingChart, setLoadingChart] = useState(true);
  const [loadingAnalysis, setLoadingAnalysis] = useState(true);
  const initialFetchDone = useRef(false);

  const mapDataToComponentFormat = (data) => {

    if (!data || !data.planets_names_list) {
      return { planets: [] };
    }

    const planets = data.planets_names_list.map((planetName) => {
      const planet = data[planetName.toLowerCase()];
      return {
        name: planet.name,
        sign: planet.sign,
        degree: planet.position.toFixed(2),
        house: planet.house,
        aspects: [planet.emoji]
      };
    });

    return { planets };
  };

  const loadChatHistory = async () => {
    try {
      setIsLoadingMessages(true);
      const getChatHistory = httpsCallable(functions, 'get_chat_history');
      const result = await getChatHistory();
      
      if (result.data && result.data.length > 0) {
        setPreloadedMessages(result.data);
      } else {
        setPreloadedMessages([{
          role: 'assistant',
          content: 'Olá! Sou seu assistente astrológico pessoal. Estou aqui para ajudar você a entender melhor seu mapa astral. Como posso ajudar você hoje?'
        }]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      setPreloadedMessages([{
        role: 'assistant',
        content: 'Olá! Sou seu assistente astrológico pessoal. Como posso ajudar você hoje?'
      }]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const getAstrologicalReport = async () => {
    if (!initialFetchDone.current) setLoadingReport(true);
    if (!user) return;

    const getAstrologicalReportFn = httpsCallable(functions, 'get_astrological_report');
    try {
      const result = await getAstrologicalReportFn();
      const reportList = Array.isArray(result.data) ? result.data : [result.data];
      const data = reportList.find(r => r && r.report) || reportList[0];

      if (!data || reportList.length === 0) {
        setReportNotFound(true);
        setReportStatus(null);
      } else {
        setAwaitingCreation(false);
        setReportNotFound(false);
        setAnalysisGenerated(data.analysis_generated ?? null);
        if (data.status === 'pending' || data.status === 'processing') {
          setReport(null);
          setReportStatus(data.status);
        } else if (data.status === 'failed') {
          setReport(null);
          setReportStatus('failed');
        } else {
          setReport(mapDataToComponentFormat(data));
          setReportStatus('completed');
        }
      }
    } catch (e) {
      if (e.code === 'functions/not-found') {
        setReportNotFound(true);
      } else {
        setReportStatus('failed');
      }
    } finally {
      setLoadingReport(false);
    }
  };

  const getAstrologicalChart = async () => {
    if (!initialFetchDone.current) setLoadingChart(true);
    setChartError(null);
    if (!user) return;

    const getAstrologicalChartFn = httpsCallable(functions, 'get_astrological_chart');
    try {
      const result = await getAstrologicalChartFn();
      setSvgChart(result.data);
    } catch (e) {
      if (e.code !== 'functions/not-found') {
        setChartError(getUserFriendlyMessage(e));
      }
    } finally {
      setLoadingChart(false);
    }
  };

  const getAstrologicalAnalysis = async () => {
    if (!initialFetchDone.current) setLoadingAnalysis(true);
    if (!user) return;
    setAnalysisError(null);

    const getAstrologicalAnalysisFn = httpsCallable(functions, 'get_astrological_analysis');
    try {
      const result = await getAstrologicalAnalysisFn();
      const analysisData = typeof result.data === 'string' ? JSON.parse(result.data) : result.data;
      setAnalysis(analysisData);
    } catch (e) {
      if (e.code !== 'functions/not-found') {
        setAnalysisError(getUserFriendlyMessage(e));
      }
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleInsightClick = () => {
    setSidebarOpen(true);
  };

  const handleRetryAnalysis = async () => {
    try {
      setRetryingAnalysis(true);
      setAnalysisError(null);
      setReportStatus(null);
      const retryFn = httpsCallable(functions, 'retry_analysis');
      await retryFn();
      // Reload data
      await getAstrologicalReport();
      await getAstrologicalAnalysis();
    } catch (e) {
      setAnalysisError('A análise falhou novamente. Tente mais tarde.');
      console.error('Retry analysis failed:', e);
    } finally {
      setRetryingAnalysis(false);
    }
  };

  // Clear navigation state to prevent stale justGenerated on refresh
  useEffect(() => {
    if (location.state?.justGenerated) {
      window.history.replaceState({}, '');
    }
  }, []);

  useEffect(() => {
    if (user) {
      Promise.all([
        getAstrologicalReport(),
        getAstrologicalChart(),
        getAstrologicalAnalysis(),
        loadChatHistory(),
      ]).finally(() => {
        initialFetchDone.current = true;
      });
    }
  }, [user]);

  // Auto-refresh when report is processing or awaiting creation
  useEffect(() => {
    const shouldPoll = reportStatus === 'processing'
      || reportStatus === 'pending'
      || awaitingCreation;
    if (!shouldPoll) return;

    const interval = setInterval(() => {
      getAstrologicalReport();
      getAstrologicalChart();
      getAstrologicalAnalysis();
    }, 5000);

    // Timeout máximo de 3 minutos para evitar polling infinito
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setAwaitingCreation(false);
    }, 180000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [reportStatus, awaitingCreation]);

  const LoadingPlaceholder = () => (
    <div className="animate-pulse">
      <div className="h-8 bg-purple-100 rounded w-3/4 mb-4"></div>
      <div className="h-4 bg-purple-100 rounded w-1/2 mb-2"></div>
      <div className="h-4 bg-purple-100 rounded w-2/3"></div>
    </div>
  );

  const SectionError = ({ message, onRetry }) => (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100 text-center">
      <AlertTriangle className="w-8 h-8 text-orange-400 mx-auto mb-3" />
      <p className="text-gray-600 text-sm mb-4">{message}</p>
      <Button variant="ghost" size="sm" onClick={onRetry}>
        <RefreshCw className="w-4 h-4" />
        Tentar novamente
      </Button>
    </div>
  );

  const SectionProcessing = () => (
    <div className="bg-white rounded-2xl shadow-lg p-8 border border-purple-100 text-center">
      <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
      <h3 className="font-semibold text-gray-800 mb-2">Gerando sua an&#225;lise...</h3>
      <p className="text-gray-500 text-sm">A IA est&#225; interpretando seu mapa astral. Isso pode levar at&#233; 3 minutos.</p>
    </div>
  );

  // Derived page state machine
  const hasData = (report && report.planets?.length > 0) || analysis || svgChart;
  const isProcessing = reportStatus === 'processing' || reportStatus === 'pending';
  const isInitialLoading = loadingReport || loadingChart || loadingAnalysis;

  const pageState = useMemo(() => {
    // If loading but already has some data, show content with section loaders
    if (isInitialLoading && hasData) return 'ready';
    // If loading and no data yet, show full-page shimmer
    if (isInitialLoading && !hasData) return 'loading';
    // Just generated or report is being processed
    if (awaitingCreation || isProcessing) return 'processing';
    // Has any data to show
    if (hasData) return 'ready';
    // Failed
    if (reportStatus === 'failed') return 'error';
    if (chartError || analysisError) return 'error';
    // Nothing exists
    return 'empty';
  }, [isInitialLoading, hasData, awaitingCreation, isProcessing, reportStatus, chartError, analysisError]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      <Navbar />

      {/* Loading State */}
      {pageState === 'loading' && (
        <div className="font-body max-w-7xl mx-auto mt-8 px-4">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative mb-6">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-purple-400 animate-pulse" />
              </div>
              <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-purple-400 rounded-full animate-spin" />
            </div>
            <p className="text-gray-500">Carregando seu mapa astral...</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {pageState === 'empty' && (
        <div className="font-body max-w-7xl mx-auto mt-8 px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <div className="relative mb-8">
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <Sparkles className="w-16 h-16 text-purple-400" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 animate-pulse" />
              <div className="absolute -bottom-1 -left-3 w-5 h-5 rounded-full bg-gradient-to-br from-pink-300 to-purple-300 animate-pulse delay-300" />
            </div>

            <h1 className="font-serif text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4 text-center">
              Seu mapa astral está esperando por você
            </h1>

            <p className="text-gray-500 text-center max-w-lg mb-8 leading-relaxed">
              Descubra os segredos das estrelas sobre sua personalidade, relacionamentos e futuro.
              Gere seu mapa astral em poucos passos.
            </p>

            <Button onClick={() => navigate('/generate-map')} size="lg">
              <Sparkles className="w-5 h-5" />
              Gerar Meu Mapa Astral
            </Button>
          </motion.div>
        </div>
      )}

      {/* Processing State */}
      {pageState === 'processing' && (
        <div className="font-body max-w-7xl mx-auto mt-8 px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="py-16"
          >
            <SectionProcessing />
          </motion.div>
        </div>
      )}

      {/* Error State (global) */}
      {pageState === 'error' && !hasData && (
        <div className="font-body max-w-7xl mx-auto mt-8 px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="py-16"
          >
            <SectionError
              message={analysisError || chartError || "Não foi possível carregar seu mapa astral."}
              onRetry={() => {
                setChartError(null);
                setAnalysisError(null);
                setReportStatus(null);
                getAstrologicalReport();
                getAstrologicalChart();
                getAstrologicalAnalysis();
              }}
            />
          </motion.div>
        </div>
      )}

      {/* Content State: progressive rendering */}
      {pageState === 'ready' && (
        <>
          <div className="font-body max-w-7xl mx-auto mt-8 px-4 pb-16">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8 relative"
            >
              <div className="absolute top-0 right-0 -translate-y-8 translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 translate-y-8 -translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse pointer-events-none"></div>

              <h1 className="font-serif text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">
                {analysis?.Relatorio?.Nome || "SEU MAPA ASTRAL"}
              </h1>

              {/* Sign badges */}
              {analysis?.Relatorio?.Signos && (
                <div className="flex items-center justify-center gap-3 mb-5 flex-wrap">
                  {[
                    { label: 'Sol', signo: analysis.Relatorio.Signos.SignoSolar?.Signo, symbol: '☉' },
                    { label: 'Lua', signo: analysis.Relatorio.Signos.SignoLunar?.Signo, symbol: '☽' },
                    { label: 'Ascendente', signo: analysis.Relatorio.Signos.Ascendente?.Signo, symbol: '↑' },
                  ].map((item, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100/60 text-purple-700 text-sm font-medium">
                      <span className="text-purple-400">{item.symbol}</span>
                      {item.label} em {item.signo}
                    </span>
                  ))}
                </div>
              )}

              {analysis?.Relatorio?.Introducao && (
                <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  {analysis.Relatorio.Introducao.Descricao}
                </p>
              )}
            </motion.div>

            {/* Chart + Signos */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Chart — 2 cols */}
              <div className="lg:col-span-2 flex flex-col">
                <h2 className="font-serif text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  Mapa Natal
                </h2>
                {chartError ? (
                  <SectionError message={chartError} onRetry={getAstrologicalChart} />
                ) : (
                  <ChartWheel loading={loadingChart} svgChart={svgChart} />
                )}
              </div>

              {/* Signos — 1 col */}
              <div className="lg:col-span-1">
                <SignosSection loadingAnalysis={loadingAnalysis} analysis={analysis} vertical />
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent my-6"></div>

            {/* Analysis Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              {retryingAnalysis ? (
                <SectionProcessing />
              ) : isProcessing && !analysis ? (
                <SectionProcessing />
              ) : analysisError || reportStatus === 'failed' ? (
                <SectionError
                  message={analysisError || "A análise não está disponível."}
                  onRetry={analysisError ? getAstrologicalAnalysis : handleRetryAnalysis}
                />
              ) : isSummaryOnly ? (
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-purple-100 max-w-md mx-auto">
                  <Lock className="w-10 h-10 text-purple-400 mx-auto mb-4" />
                  <h3 className="font-serif text-lg font-semibold text-gray-800 mb-2">Análise com Inteligência Artificial</h3>
                  <p className="text-sm text-gray-500 mb-5">Assine um plano para desbloquear a interpretação completa do seu mapa astral com IA — planetas, casas e aspectos detalhados.</p>
                  <Button onClick={() => navigate('/pricing')} fullWidth>
                    Ver Planos
                  </Button>
                </div>
              ) : !analysis && analysisGenerated === false ? (
                <div className="bg-white rounded-2xl shadow-lg p-8 text-center border border-purple-100 max-w-md mx-auto">
                  <Sparkles className="w-10 h-10 text-purple-500 mx-auto mb-4" />
                  <h3 className="font-serif text-lg font-semibold text-gray-800 mb-2">Análise com Inteligência Artificial</h3>
                  <p className="text-sm text-gray-500 mb-5">Seu mapa foi gerado. Agora gere a interpretação completa com IA — planetas, casas, aspectos e muito mais.</p>
                  <Button
                    onClick={handleRetryAnalysis}
                    disabled={retryingAnalysis}
                    loading={retryingAnalysis}
                    loadingText="Gerando..."
                    fullWidth
                  >
                    <Sparkles className="w-5 h-5" />
                    Gerar Análise com IA
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <PlanetasSection
                    loadingAnalysis={loadingAnalysis}
                    analysis={analysis}
                  />
                  <HousesSection
                    loading={loadingAnalysis}
                    houses={analysis?.Relatorio?.CasasAstrologicas}
                  />
                </div>
              )}
            </motion.div>

            {/* Conclusion Section */}
            {!isSummaryOnly && !analysisError && (
              loadingAnalysis ? (
                <motion.div className="mt-8 bg-white rounded-2xl shadow-lg p-6 border border-purple-100">
                  <LoadingPlaceholder />
                </motion.div>
              ) : (
                analysis?.Relatorio?.Conclusao && (
                  <>
                    <div className="h-px bg-gradient-to-r from-transparent via-purple-300/40 to-transparent my-10"></div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100"
                    >
                      <h2 className="font-serif text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                        Conclusão
                      </h2>
                      <div className="font-body space-y-4">
                        <p className="text-gray-600 leading-relaxed">{analysis.Relatorio.Conclusao.Descricao}</p>
                        <p className="text-sm text-gray-500 italic leading-relaxed">{analysis.Relatorio.Conclusao.Observacao}</p>
                      </div>
                    </motion.div>
                  </>
                )
              )
            )}
            {/* Redo Map — only for plans with more than 1 map */}
            {limits.own_maps > 1 && (
            <div className="mt-8 text-center">
              {isAtLimit('own_maps') ? (
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100 max-w-md mx-auto">
                  <p className="text-gray-500 text-sm mb-4">Limite de mapas atingido. Faça upgrade para gerar mais mapas.</p>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="px-6 py-2 border border-purple-300 text-purple-600 rounded-xl text-sm font-medium hover:bg-purple-50 transition-colors"
                  >
                    Ver Planos
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/generate-map?redo=true')}
                  className="px-6 py-2.5 border border-purple-300 text-purple-600 rounded-xl font-medium hover:bg-purple-50 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 inline mr-2" />
                  Refazer Mapa Astral
                </button>
              )}
            </div>
            )}
          </div>
          <AstroInsightButton onClick={handleInsightClick} />
          <InsightSidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            loading={isLoadingMessages}
            preloadedMessages={preloadedMessages}
          />
        </>
      )}
    </div>
  );
};

export default AstroChartDisplay;